import {
  type Design,
  type DesignSearchResponse,
  type NormalizedDrawing,
  type NormalizedDrawingResponse,
  fetcher,
  getThumbnailImage,
} from "@repo/api";
import { useSettings } from "@repo/ui";
import useSWR from "swr";

const normalizeDesign = (design: Design): NormalizedDrawing => {
  return {
    id: design.designId,
    image: getThumbnailImage(design),
    name: design.designName || design.designId,
    value: design.amount,
    created: design.creationTimestamp,
    updated: design.lastUpdatedTimestamp,
    type: design.plannerType ?? design.sourceApi,
    statusDescription: undefined,
    editUrl: design.designUrlEdit,
    viewUrl: design.designUrlView,
    isVPCDrawing: design.sourceApi === "VPC",
    isDexfDrawing: design.sourceApi === "DEXF",
    items: design.items,
    statusCodeTranslated: design.statusCodeTranslated,
    statusCodeNotTranslated: design.statusCode,
    applicationId: design.applicationId,
    addedToProjectValue: design.isIncludedInOppValue,
    designUrlNew: design.designUrlNew,
    shortcode: design.shortcode,
  };
};

const getCustomerType = (customer: Customer) => {
  if (customer.customerType === "Individual") {
    return "Private";
  }
  if (customer.customerType === "Business") {
    return "Business";
  }
  return customer.customerType;
};

const getSearchParam = (
  searchTerm: string | undefined,
  customer: Customer | undefined
) => {
  if (searchTerm) {
    return { searchKey: "searchTerm", searchValue: searchTerm };
  } else if (customer) {
    return customer?.customerAccountState === "REGISTERED"
      ? {
          searchKey: "customerIdentifier",
          searchValue: {
            partyId: customer?.partyId,
            customerType: getCustomerType(customer),
          },
        }
      : { searchKey: "customerEmail", searchValue: customer?.email };
  }
  return { searchKey: "searchTerm", searchValue: undefined };
};

export function useSearchDesign(
  searchTerm: string | undefined,
  customer: Customer | undefined
) {
  const { market, language } = useSettings();
  const { searchKey, searchValue } = getSearchParam(searchTerm, customer);

  return useSWR<NormalizedDrawingResponse>(
    searchValue ? `/designs/search/${searchTerm}/${customer?.partyId}` : null,
    async () => {
      const res = await fetcher.global.post<DesignSearchResponse, false>(
        "/designs/search",
        {
          searchBy: {
            [searchKey]: searchValue,
          },
          countryCode: market.toUpperCase(),
          language,
        }
      );

      return {
        plannerList:
          res?.designGroups?.map((group) => ({
            name: group.plannerType,
            applicationId: group.applicationId,
            designUrlNew: group.designUrlNew,
            designList: group.designs?.map(normalizeDesign) ?? [],
          })) ?? [],
      };
    },
    { revalidateOnFocus: false }
  );
}

export function useSearchDesignById(designId?: string, applicationId?: string) {
  const { market, language } = useSettings();

  return useSWR<NormalizedDrawing | undefined>(
    designId ? `/designs/searchByCode/${designId}` : null,
    async () => {
      const res = await fetcher.global.post<DesignSearchResponse, false>(
        "/designs/search",
        {
          searchBy: {
            designId,
            applicationId,
          },
          countryCode: market.toUpperCase(),
          language,
        }
      );

      // find the design inside the groups
      const design = res?.designGroups
        ?.flatMap((group) => group.designs ?? [])
        .find((d) => d.designId === designId);

      return design ? normalizeDesign(design) : undefined;
    },
    { revalidateOnFocus: false }
  );
}
